// Group create/edit. One screen, two modes:
//   #/group/new   → create a group (name + pick members from your friends)
//   #/group/:id   → edit a group you own (rename, add/remove members, delete)
// Only the owner can reach edit; the server enforces it too.

import $ from 'jquery';
import type { GroupSummary } from '@shared/dto';
import type { User } from '@shared/models';
import type { Screen } from './types';
import { api } from '../api';
import { navigate } from '../router';
import { getCurrentUser } from '../session';
import { avatarHtml, esc } from '../ui/dom';
import { errorHtml, loadingHtml } from '../ui/states';
import { toast } from '../ui/toast';
import { confirmDialog } from '../ui/confirm';

function memberRowHtml(u: User, ownerId: string): string {
  const isOwner = u.id === ownerId;
  return `<div class="roster__row">
    ${avatarHtml(u.name, u.avatarUrl, 36)}
    <span class="roster__name">${esc(u.name)}</span>
    ${
      isOwner
        ? `<span class="badge" style="margin-left:auto">Sahip</span>`
        : `<button class="btn btn--ghost btn--sm icon-btn" data-remove-member="${esc(u.id)}" aria-label="Çıkar" title="Çıkar">✕</button>`
    }
  </div>`;
}

function friendChipHtml(u: User, attr: string): string {
  return `<button type="button" class="chip" ${attr}="${esc(u.id)}">${esc(u.name)}</button>`;
}

// --- Create mode ----------------------------------------------------------

async function renderCreate(body: JQuery): Promise<void> {
  const friends = await api.call('friends.mine', undefined);
  const selected = new Set<string>();

  body.html(`
    <div class="groupedit">
      <label class="field">
        <span class="field__label">Grup adı</span>
        <input class="input" id="grp-name" placeholder="örn. Çankaya Ballers" autocomplete="off" />
      </label>
      <div class="section-title">Üyeler</div>
      ${
        friends.length
          ? `<div class="chips" id="member-pick">${friends.map((u) => friendChipHtml(u, 'data-pick')).join('')}</div>`
          : `<p class="muted">Önce arkadaş eklemelisin — gruplar arkadaşlarından oluşur.</p>`
      }
      <button class="btn btn--primary btn--block" id="grp-save" style="margin-top:var(--s-4)">Grubu oluştur</button>
    </div>
  `);

  body.on('click', '[data-pick]', function () {
    const id = $(this).data('pick') as string;
    if (selected.has(id)) selected.delete(id);
    else selected.add(id);
    $(this).toggleClass('is-on');
  });

  $('#grp-save').on('click', async () => {
    const name = ($('#grp-name').val() as string) ?? '';
    if (!name.trim()) {
      toast('Grup adı gir', 'error');
      return;
    }
    try {
      await api.call('groups.create', { name, memberIds: [...selected] });
      toast('Grup oluşturuldu 👥', 'success');
      navigate('/friends');
    } catch (err) {
      toast(err instanceof Error ? err.message : String(err), 'error');
    }
  });
}

// --- Edit mode ------------------------------------------------------------

async function renderEdit(body: JQuery, groupId: string): Promise<void> {
  const meId = getCurrentUser()?.id ?? '';
  const [groups, friends] = await Promise.all([
    api.call('groups.mine', undefined),
    api.call('friends.mine', undefined),
  ]);
  const group = groups.find((g) => g.id === groupId);
  if (!group) {
    body.html(errorHtml('Grup bulunamadı.'));
    return;
  }
  if (group.ownerId !== meId) {
    body.html(errorHtml('Bu grubu düzenleme yetkin yok.'));
    return;
  }

  const draw = (g: GroupSummary): void => {
    const memberIds = new Set(g.members.map((m) => m.id));
    const addable = friends.filter((f) => !memberIds.has(f.id));
    body.html(`
      <div class="groupedit">
        <label class="field">
          <span class="field__label">Grup adı</span>
          <div class="field--row">
            <input class="input" id="grp-name" value="${esc(g.name)}" autocomplete="off" />
            <button class="btn btn--ghost btn--sm" id="grp-rename">Kaydet</button>
          </div>
        </label>

        <div class="section-title">Üyeler (${g.members.length})</div>
        <div class="roster">${g.members.map((m) => memberRowHtml(m, g.ownerId)).join('')}</div>

        ${
          addable.length
            ? `<div class="section-title">Üye ekle</div>
               <div class="chips" id="member-add">${addable.map((u) => friendChipHtml(u, 'data-add-member')).join('')}</div>`
            : ''
        }

        <button class="btn btn--danger btn--block" id="grp-delete" style="margin-top:var(--s-6)">Grubu sil</button>
      </div>
    `);
  };
  draw(group);

  // Delegated handlers (survive the re-render draw() does on membership changes).
  body.on('click', '#grp-rename', async () => {
    const name = ($('#grp-name').val() as string) ?? '';
    try {
      const updated = await api.call('groups.rename', { groupId, name });
      Object.assign(group, updated);
      toast('Grup adı güncellendi', 'success');
    } catch (err) {
      toast(err instanceof Error ? err.message : String(err), 'error');
    }
  });

  body.on('click', '[data-add-member]', async function () {
    const memberId = $(this).data('add-member') as string;
    try {
      const updated = await api.call('groups.addMembers', { groupId, memberIds: [memberId] });
      Object.assign(group, updated);
      draw(group);
    } catch (err) {
      toast(err instanceof Error ? err.message : String(err), 'error');
    }
  });

  body.on('click', '[data-remove-member]', async function () {
    const memberId = $(this).data('remove-member') as string;
    const member = group.members.find((m) => m.id === memberId);
    const ok = await confirmDialog({
      title: 'Üyeyi çıkar',
      message: member
        ? `${member.name} gruptan çıkarılacak.`
        : 'Bu üye gruptan çıkarılacak.',
      confirmLabel: 'Çıkar',
    });
    if (!ok) return;
    try {
      const updated = await api.call('groups.removeMember', { groupId, memberId });
      Object.assign(group, updated);
      draw(group);
    } catch (err) {
      toast(err instanceof Error ? err.message : String(err), 'error');
    }
  });

  body.on('click', '#grp-delete', async () => {
    const ok = await confirmDialog({
      title: 'Grubu sil',
      message: `"${group.name}" grubu kalıcı olarak silinecek. Bu işlem geri alınamaz.`,
      confirmLabel: 'Sil',
    });
    if (!ok) return;
    await api.call('groups.delete', { groupId });
    toast('Grup silindi', 'info');
    navigate('/friends');
  });
}

export const GroupEditScreen: Screen = async (body, params) => {
  body.html(loadingHtml());
  if (params.id === 'new') await renderCreate(body);
  else await renderEdit(body, params.id);
};
